import {
    Box,
    IconButton,
    InputBase,
    MenuItem,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    Autocomplete,
    TextField,
    Collapse,
    Badge,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from "@mui/icons-material";
import {useState, useEffect} from "react";
import {getCompanies} from "../../services/system/CompanyService";
import {RTL} from "../../cache/CacheRtl";

const CouponSearchBar = ({
                             updateKeyword,
                             keyword,
                             updateCompanyId,
                             companyId,
                             updateIsActive,
                             isActive
                         }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [filtersExpanded, setFiltersExpanded] = useState(false);

    // Calculate active filter count for badge
    const activeFilterCount = [
        selectedCompany !== null,
        isActive !== null
    ].filter(Boolean).length;

    useEffect(() => {
        // Load companies for the dropdown
        getCompanies()
            .then((res) => {
                setCompanies(res.data || []);
            })
            .catch((e) => {
                console.log("Error loading companies: ", e);
            });
    }, []);

    useEffect(() => {
        // Update selected company when companyId changes
        if (companyId && companies.length > 0) {
            const company = companies.find(c => c.id === companyId);
            setSelectedCompany(company || null);
        } else {
            setSelectedCompany(null);
        }
    }, [companyId, companies]);

    return (
        <Box sx={{ padding: { xs: "0 12px 12px 12px", md: "0 20px 20px 20px" } }}>
            {/* Filters */}
            <Paper
                sx={{
                    backgroundColor: "#FCFCFD",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    padding: { xs: "12px", md: "20px" },
                    direction: "rtl"
                }}
                elevation={0}
            >
                {/* Mobile: Search + Filter Toggle */}
                {isMobile ? (
                    <>
                        <Box sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            marginBottom: filtersExpanded ? "12px" : 0
                        }}>
                            {/* Search Input */}
                            <Box sx={{
                                backgroundColor: "#FFFFFF",
                                border: "1px solid #E2E8F0",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                flex: 1,
                                height: "40px"
                            }}>
                                <IconButton sx={{ padding: "8px", color: "#64748B" }}>
                                    <SearchIcon fontSize="small" />
                                </IconButton>
                                <InputBase
                                    value={keyword}
                                    onChange={(e) => updateKeyword(e.target.value)}
                                    sx={{
                                        flex: 1,
                                        fontSize: "14px",
                                        "& input::placeholder": {
                                            color: "#94A3B8"
                                        }
                                    }}
                                    placeholder="חיפוש קופון"
                                />
                            </Box>

                            {/* Filter Toggle Button */}
                            <Box
                                onClick={() => setFiltersExpanded(!filtersExpanded)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    padding: "8px 12px",
                                    backgroundColor: filtersExpanded ? "#E0F2F1" : "#FFFFFF",
                                    border: "1px solid #E2E8F0",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    height: "40px"
                                }}
                            >
                                <Badge
                                    badgeContent={activeFilterCount}
                                    color="primary"
                                    sx={{
                                        "& .MuiBadge-badge": {
                                            fontSize: "10px",
                                            height: "16px",
                                            minWidth: "16px"
                                        }
                                    }}
                                >
                                    <FilterIcon sx={{ fontSize: 18, color: '#64748B' }} />
                                </Badge>
                                {filtersExpanded ?
                                    <ExpandLessIcon sx={{ fontSize: 16, color: "#64748B" }} /> :
                                    <ExpandMoreIcon sx={{ fontSize: 16, color: "#64748B" }} />
                                }
                            </Box>
                        </Box>

                        {/* Collapsible Filters */}
                        <Collapse in={filtersExpanded}>
                            <Box sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5
                            }}>
                                {/* Company Filter */}
                                <RTL>
                                    <Autocomplete
                                        value={selectedCompany}
                                        onChange={(event, value) => {
                                            if (value == null) {
                                                updateCompanyId(null);
                                            } else {
                                                updateCompanyId(value.id);
                                            }
                                        }}
                                        options={companies}
                                        getOptionLabel={(option) => option.name || ""}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="חברה"
                                                size="small"
                                                sx={{
                                                    backgroundColor: "#FFFFFF",
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            borderColor: "#E2E8F0"
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: "#CBD5E1"
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: "#64748B"
                                                        }
                                                    },
                                                    "& .MuiInputLabel-root": {
                                                        fontSize: "14px",
                                                        color: "#64748B",
                                                        "&.Mui-focused": {
                                                            color: "#64748B"
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                        sx={{
                                            "& .MuiAutocomplete-clearIndicator": {
                                                color: "#64748B"
                                            },
                                            "& .MuiAutocomplete-popupIndicator": {
                                                color: "#64748B"
                                            }
                                        }}
                                    />
                                </RTL>

                                {/* Status Filter */}
                                <RTL>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel
                                            sx={{
                                                fontSize: "14px",
                                                color: "#64748B",
                                                "&.Mui-focused": { color: "#64748B" }
                                            }}
                                        >
                                            סטטוס
                                        </InputLabel>
                                        <Select
                                            value={isActive === null ? "all" : isActive}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                updateIsActive(value === "all" ? null : value);
                                            }}
                                            label="סטטוס"
                                            sx={{
                                                backgroundColor: "#FFFFFF",
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#E2E8F0"
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#CBD5E1"
                                                },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#64748B"
                                                }
                                            }}
                                        >
                                            <MenuItem value="all">
                                                <Typography sx={{ fontSize: "14px" }}>הכול</Typography>
                                            </MenuItem>
                                            <MenuItem value={true}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Box
                                                        sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: "50%",
                                                            backgroundColor: "#10B981"
                                                        }}
                                                    />
                                                    <Typography sx={{ fontSize: "14px" }}>פעיל</Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value={false}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Box
                                                        sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: "50%",
                                                            backgroundColor: "#6B7280"
                                                        }}
                                                    />
                                                    <Typography sx={{ fontSize: "14px" }}>לא פעיל</Typography>
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Box>
                        </Collapse>
                    </>
                ) : (
                    <>
                        {/* Desktop: Header */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "20px",
                                gap: 1
                            }}
                        >
                            <FilterIcon sx={{ color: "#64748B", fontSize: "20px" }} />
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "16px",
                                    color: "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}
                            >
                                סינון קופונים
                            </Typography>
                        </Box>

                        {/* Desktop: Filters Container */}
                        <Box sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "center",
                            flexWrap: "wrap"
                        }}>
                            {/* Search Input */}
                            <Box sx={{
                                backgroundColor: "#FFFFFF",
                                border: "1px solid #E2E8F0",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                minWidth: "280px"
                            }}>
                                <IconButton sx={{ padding: "12px", color: "#64748B" }}>
                                    <SearchIcon fontSize="small" />
                                </IconButton>
                                <InputBase
                                    value={keyword}
                                    onChange={(e) => updateKeyword(e.target.value)}
                                    sx={{
                                        flex: 1,
                                        fontSize: "14px",
                                        "& input::placeholder": {
                                            color: "#94A3B8"
                                        }
                                    }}
                                    placeholder="חיפוש לפי קוד קופון"
                                />
                            </Box>

                            {/* Company Filter */}
                            <RTL>
                                <Box sx={{ minWidth: "220px" }}>
                                    <Autocomplete
                                        value={selectedCompany}
                                        onChange={(event, value) => {
                                            if (value == null) {
                                                updateCompanyId(null);
                                            } else {
                                                updateCompanyId(value.id);
                                            }
                                        }}
                                        options={companies}
                                        getOptionLabel={(option) => option.name || ""}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="חברה"
                                                size="small"
                                                sx={{
                                                    backgroundColor: "#FFFFFF",
                                                    "& .MuiOutlinedInput-root": {
                                                        "& fieldset": {
                                                            borderColor: "#E2E8F0"
                                                        },
                                                        "&:hover fieldset": {
                                                            borderColor: "#CBD5E1"
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            borderColor: "#64748B"
                                                        }
                                                    },
                                                    "& .MuiInputLabel-root": {
                                                        fontSize: "14px",
                                                        color: "#64748B",
                                                        "&.Mui-focused": {
                                                            color: "#64748B"
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                        sx={{
                                            "& .MuiAutocomplete-clearIndicator": {
                                                color: "#64748B"
                                            },
                                            "& .MuiAutocomplete-popupIndicator": {
                                                color: "#64748B"
                                            }
                                        }}
                                    />
                                </Box>
                            </RTL>

                            {/* Status Filter */}
                            <RTL>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel
                                        sx={{
                                            fontSize: "14px",
                                            color: "#64748B",
                                            "&.Mui-focused": { color: "#64748B" }
                                        }}
                                    >
                                        סטטוס
                                    </InputLabel>
                                    <Select
                                        value={isActive === null ? "all" : isActive}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            updateIsActive(value === "all" ? null : value);
                                        }}
                                        label="סטטוס"
                                        sx={{
                                            backgroundColor: "#FFFFFF",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#E2E8F0"
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#CBD5E1"
                                            },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#64748B"
                                            }
                                        }}
                                    >
                                        <MenuItem value="all">
                                            <Typography sx={{ fontSize: "14px" }}>הכול</Typography>
                                        </MenuItem>
                                        <MenuItem value={true}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        backgroundColor: "#10B981"
                                                    }}
                                                />
                                                <Typography sx={{ fontSize: "14px" }}>פעיל</Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value={false}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        backgroundColor: "#6B7280"
                                                    }}
                                                />
                                                <Typography sx={{ fontSize: "14px" }}>לא פעיל</Typography>
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </RTL>
                        </Box>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default CouponSearchBar;